import React from 'react';
import { Pagination } from 'react-bootstrap';
import { PaginationResponse } from '../../types/pagination.types';

export interface PaginationComponentProps {
  OnPageClick: (Page: number) => void;
  PaginationData: PaginationResponse;
}

const PaginationComponent = ({
  OnPageClick,
  PaginationData,
}: PaginationComponentProps) => {
  const pageNumberThreshold = 6 + 1;
  const pageDifferenceStart =
    PaginationData.totalPages - PaginationData.currentPage < 3 ? 3 : 2;
  const pageDifferenceEnd = PaginationData.currentPage - 1 > 3 ? 2 : 4;

  const HandlePageClick = (Page: number) => {
    OnPageClick(Page);
  };

  return (
    <div className="my-32">
      <Pagination className="justify-content-center mb-0">
        <Pagination.Prev
          className="page-item-prev"
          disabled={
            !PaginationData.hasPreviousPage || PaginationData.currentPage === 1
          }
          onClick={() => HandlePageClick(PaginationData.currentPage - 1)}
        />
        {/* When pages total is max 6, show all pagination items */}
        {PaginationData.totalPages < pageNumberThreshold &&
          [...Array(PaginationData.totalPages)].map((e, i: number) => {
            return (
              <Pagination.Item
                key={i.toString() + i.toString()}
                disabled={PaginationData.currentPage === i + 1}
                onClick={() => HandlePageClick(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            );
          })}
        {PaginationData.totalPages >= pageNumberThreshold && (
          <>
            {/* Always show first page */}
            <Pagination.Item
              disabled={PaginationData.currentPage === 1}
              onClick={() => HandlePageClick(1)}
            >
              1
            </Pagination.Item>

            {/* Separator beside 1 */}
            {PaginationData.currentPage > 3 && (
              <Pagination.Ellipsis disabled className="page-item-ellipsis" />
            )}

            {/* Show pages before current page */}
            {PaginationData.currentPage > 2 &&
              [...Array(PaginationData.totalPages)].map((e, i: number) => {
                return (
                  <React.Fragment key={i.toString() + i.toString()}>
                    {i + 1 !== 1 &&
                      i + 1 < PaginationData.currentPage &&
                      i + 1 >
                        PaginationData.currentPage - pageDifferenceStart && (
                        <Pagination.Item onClick={() => HandlePageClick(i + 1)}>
                          {i + 1}
                        </Pagination.Item>
                      )}
                  </React.Fragment>
                );
              })}

            {/* Show current page if it isn't first or last */}
            {PaginationData.currentPage > 1 &&
              PaginationData.currentPage < PaginationData.totalPages && (
                <Pagination.Item disabled>
                  {PaginationData.currentPage}
                </Pagination.Item>
              )}

            {/* Show pages after current page */}
            {PaginationData.totalPages - PaginationData.currentPage > 1 &&
              [...Array(PaginationData.totalPages)].map((e, i: number) => {
                return (
                  <React.Fragment key={i.toString() + i.toString()}>
                    {i + 1 !== PaginationData.totalPages &&
                      i + 1 > PaginationData.currentPage &&
                      i + 1 - PaginationData.currentPage <
                        pageDifferenceEnd && (
                        <Pagination.Item onClick={() => HandlePageClick(i + 1)}>
                          {i + 1}
                        </Pagination.Item>
                      )}
                  </React.Fragment>
                );
              })}

            {/* Separator beside last item */}
            {PaginationData.totalPages - PaginationData.currentPage > 2 && (
              <Pagination.Ellipsis disabled className="page-item-ellipsis" />
            )}

            {/* Always show last page */}
            <Pagination.Item
              disabled={
                PaginationData.currentPage === PaginationData.totalPages
              }
              onClick={() => HandlePageClick(PaginationData.totalPages)}
            >
              {PaginationData.totalPages}
            </Pagination.Item>
          </>
        )}
        <Pagination.Next
          className="page-item-next"
          disabled={
            !PaginationData.hasNextPage ||
            PaginationData.currentPage === PaginationData.totalPages
          }
          onClick={() => HandlePageClick(PaginationData.currentPage + 1)}
        />
      </Pagination>
    </div>
  );
};

export default PaginationComponent;
export { PaginationComponent };
